import React from 'react';
import { Home, Building2, Tractor, ArrowRight, Wifi, Leaf, TrendingUp, Shield, Zap, Sun } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Services = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: Home,
      title: 'Instalación Residencial',
      subtitle: 'Para tu hogar',
      description: 'Transforma tu hogar en una fuente de energía limpia. Reducí desde el 85% al 100% de tu factura eléctrica mientras aumentás el valor de tu propiedad con tecnología de última generación.',
      features: [
        { icon: Zap, text: 'Sistemas desde 3kW personalizados' },
        { icon: Shield, text: 'Garantía de 25 años en paneles' },
        { icon: Sun, text: 'Instalación profesional en 3-5 días' },
      ],
      highlights: [
        'Ahorro visible desde el primer mes',
        'Aumentá el valor de tu propiedad',
        'Energía limpia y sustentable para tu familia'
      ],
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800',
      link: '/servicios/residenciales',
      cta: 'Descubrí cómo ahorrar',
      accentColor: '#90EE90',
    },
    {
      icon: Building2,
      title: 'Empresas e Industrias',
      subtitle: 'Comercial & Industrial',
      description: 'Potenciá tu negocio con energía solar y convertite en referente de sustentabilidad. Reducí costos operativos, mejorá tu imagen corporativa y accedé a beneficios fiscales exclusivos.',
      features: [
        { icon: TrendingUp, text: 'Ahorro visible desde el primer mes' },
        { icon: Leaf, text: 'Marketing verde para tu marca' },
        { icon: Shield, text: 'Asesoría en beneficios tributarios' },
      ],
      highlights: [
        'Reducción de impuestos por sustentabilidad',
        'Diferenciación competitiva en el mercado',
        'Gestión legal y técnica profesional incluida'
      ],
      image: 'https://images.unsplash.com/photo-1497440001374-f26997328c1b?w=800',
      link: '/servicios/empresas',
      cta: 'Quiero potenciar mi empresa',
      accentColor: '#90EE90',
    },
    {
      icon: Tractor,
      title: 'Soluciones Rurales y Agro',
      subtitle: 'Campo & Estancias',
      description: 'Olvidate de los cortes de luz y del alto costo de la ANDE. Llevamos energía confiable a las zonas más alejadas de Paraguay, con opciones de conectividad satelital disponibles.',
      features: [
        { icon: Zap, text: 'Independencia total de la red eléctrica' },
        { icon: Wifi, text: 'Internet satelital (opcional, costo aparte)' },
        { icon: Shield, text: 'Precios accesibles y financiamiento' },
      ],
      highlights: [
        'Adiós a los cortes de luz constantes',
        'Conectividad disponible donde la necesites',
        'Soluciones diseñadas para el campo paraguayo'
      ],
      image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=800',
      link: '/servicios/rural-agro',
      cta: 'Llevá energía a tu campo',
      accentColor: '#90EE90',
    },
  ];

  const handleNavigate = (link) => {
    navigate(link);
    // Asegurar scroll al top de la página después de navegar
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section id="services" className="py-16 sm:py-24 md:py-32 bg-gradient-to-b from-white to-zinc-50" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
            Nuestros Servicios
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-zinc-900 mb-4 sm:mb-6">
            Soluciones para Cada Necesidad
          </h2>
          <p className="text-base sm:text-lg text-zinc-600 max-w-2xl mx-auto px-2">
            Desde tu hogar hasta tu empresa o estancia en el campo, diseñamos sistemas solares 
            que transforman la forma en que consumís energía.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={index}
                className="bg-white border border-zinc-200 rounded-2xl overflow-hidden hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 group cursor-pointer flex flex-col"
                onClick={() => handleNavigate(service.link)}
                data-testid={`service-card-${index}`}
              >
                {/* Image */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                  
                  {/* Icon Badge */}
                  <div className="absolute top-4 left-4 p-3 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
                    <Icon className="text-[#90EE90]" size={24} />
                  </div>
                  
                  {/* Title on Image */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="text-xs font-medium uppercase tracking-wider text-[#90EE90]">
                      {service.subtitle}
                    </span>
                    <h3 className="text-2xl font-bold text-white mt-1">{service.title}</h3>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 flex-1 flex flex-col">
                  <p className="text-zinc-600 leading-relaxed mb-6 text-sm">
                    {service.description}
                  </p>

                  {/* Features */}
                  <div className="space-y-3 mb-6">
                    {service.features.map((feature, idx) => {
                      const FeatureIcon = feature.icon;
                      return (
                        <div key={idx} className="flex items-center gap-3 text-sm">
                          <div className="p-1.5 bg-[#90EE90]/10 rounded-lg">
                            <FeatureIcon className="w-4 h-4 text-[#14532D]" />
                          </div>
                          <span className="text-zinc-700">{feature.text}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Highlights (for Commercial and Rural) */}
                  {service.highlights && (
                    <div className="bg-zinc-50 rounded-xl p-4 mb-6 border border-zinc-100">
                      <p className="text-xs font-semibold uppercase tracking-wider text-[#14532D] mb-3">
                        Beneficios exclusivos
                      </p>
                      <ul className="space-y-2">
                        {service.highlights.map((highlight, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-xs text-zinc-600">
                            <div className="w-1.5 h-1.5 bg-[#90EE90] rounded-full mt-1.5 flex-shrink-0"></div>
                            {highlight}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* CTA - Push to bottom */}
                  <div className="mt-auto">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNavigate(service.link);
                      }}
                      className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-zinc-900 hover:bg-[#14532D] text-white font-medium rounded-xl transition-all duration-300 group/btn"
                      data-testid={`service-cta-${index}`}
                    >
                      {service.cta}
                      <ArrowRight size={18} className="group-hover/btn:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-zinc-500 mb-4">¿No estás seguro de qué solución necesitás?</p>
          <button
            onClick={() => {
              navigate('/calculadora');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#90EE90] hover:bg-[#7DD87D] text-[#14532D] font-bold rounded-xl transition-all duration-300 hover:scale-105"
          >
            Calculá tu sistema ideal
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Services;
